import React, { useCallback, useEffect, useRef, useState } from 'react';
import MessageBubble from './MessageBubble';
import AgentInlineDeposit from './AgentInlineDeposit';
import VoiceModePanel from './VoiceModePanel';
import { AgentProduct, ChatMessage, RequiresAction } from '../../api/bookingAgent';
import { getShopifyCartRoot, openShopifyCartDrawer } from '../../utils/shopifyCart';
import {
  applyTextareaResize,
  CHAT_TEXTAREA_MAX_HEIGHT,
  CHAT_TEXTAREA_MIN_HEIGHT,
  resetTextareaResize,
} from './chatInputUtils';
import {
  AGENT_OTP_LENGTH,
  getOtpFocusIndex,
  mergeOtpDigit,
  parseOtpDigits,
} from './otpInputUtils';

interface ChatWindowProps {
  messages: ChatMessage[];
  loading: boolean;
  requiresAction: RequiresAction | null;
  onSend: (text: string, images?: string[]) => void;
  onVerifyOtp?: (code: string) => Promise<boolean>;
  sessionId?: string | null;
  onDepositPaid?: () => void;
  onAddToCart?: (product: AgentProduct) => Promise<{ success: boolean; error?: string }>;
  cartState?: Record<string, CartItemState>;
  cartCount?: number | null;
  voiceSupported?: boolean;
  voiceMode?: boolean;
  isListening?: boolean;
  isSpeaking?: boolean;
  isAwaitingReply?: boolean;
  onToggleVoice?: () => void;
  voiceError?: string | null;
}

const MAX_IMAGE_DIMENSION = 1024;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_IMAGES = 5;
const PHOTO_INPUT_ID = 'husl-agent-photo-input';
const OTP_LENGTH = AGENT_OTP_LENGTH;
const OTP_RESEND_SECONDS = 60;

export type CartItemState = 'adding' | 'added' | 'error';

interface ProductCardsInlineProps {
  products: AgentProduct[];
  onAddToCart?: (product: AgentProduct) => Promise<{ success: boolean; error?: string }>;
  // Lifted to ChatWidget so add progress survives this block unmounting
  // (requires_action swaps, voice/text toggles) and re-adds are blocked.
  cartState?: Record<string, CartItemState>;
  containerStyle?: React.CSSProperties;
}

export function ProductCardsInline({ products, onAddToCart, cartState = {}, containerStyle }: ProductCardsInlineProps) {
  const [cartError, setCartError] = useState('');

  const handleAdd = async (product: AgentProduct) => {
    if (!onAddToCart || !product.variant_id) return;
    setCartError('');
    const result = await onAddToCart(product);
    if (!result.success) {
      setCartError(result.error || 'Could not add to cart.');
    }
  };

  const anyAdded = products.some(
    (product) => product.variant_id && cartState[product.variant_id] === 'added',
  );

  return (
    <div style={{ margin: '4px 0 14px 36px', display: 'flex', flexDirection: 'column', gap: 8, ...containerStyle }}>
      {products.map((product) => {
        const key = product.variant_id || product.product_id;
        const state = product.variant_id ? cartState[product.variant_id] : undefined;
        return (
          <div
            key={key}
            style={{
              display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px',
              background: '#fdf8f0', borderRadius: 12, border: '1px solid #e8d5b0',
            }}
          >
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, background: '#fff', flexShrink: 0 }}
              />
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#3a3530' }}>{product.name}</div>
              {product.brand ? (
                <div style={{ fontSize: 12, color: '#8a7f73' }}>{product.brand}</div>
              ) : null}
              {product.reason ? (
                <div style={{ fontSize: 12, color: '#6b5f54', marginTop: 2 }}>{product.reason}</div>
              ) : null}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#3a3530', marginTop: 4 }}>
                {(product.currency || 'AUD').toUpperCase()} {product.price}
              </div>
            </div>
            {onAddToCart && product.variant_id ? (
              <button
                onClick={() => handleAdd(product)}
                disabled={state === 'adding' || state === 'added'}
                style={{
                  padding: '7px 14px', borderRadius: 20, border: 'none', flexShrink: 0,
                  background: state === 'added' ? '#1b6b35' : '#2d2925', color: '#fff',
                  fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                  cursor: state === 'adding' || state === 'added' ? 'default' : 'pointer',
                  opacity: state === 'adding' ? 0.7 : 1,
                }}
              >
                {state === 'added' ? '✓ Added' : state === 'adding' ? 'Adding…' : 'Add to cart'}
              </button>
            ) : null}
          </div>
        );
      })}
      {cartError ? (
        <div style={{
          fontSize: 12, color: '#b3261e', background: '#fdf0ef',
          border: '1px solid #f0c9c5', borderRadius: 8, padding: '6px 10px',
        }}>
          {cartError}
          {/variant/i.test(cartError)
            ? ' — this product may not be available in the online store right now.'
            : ''}
        </div>
      ) : null}
      {anyAdded ? (
        <button
          onClick={() => {
            if (!openShopifyCartDrawer()) {
              window.location.href = `${getShopifyCartRoot()}cart`;
            }
          }}
          style={{
            fontSize: 12, color: '#2d2925', fontWeight: 600, textDecoration: 'underline',
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: 'inherit', alignSelf: 'flex-start',
          }}
        >
          View cart
        </button>
      ) : null}
    </div>
  );
}

interface OtpInlineProps {
  mobile?: string;
  onVerify: (code: string) => Promise<boolean>;
}

export function OtpInline({ mobile, onVerify }: OtpInlineProps) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [secsLeft, setSecsLeft] = useState(OTP_RESEND_SECONDS);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (secsLeft <= 0) return;
    const timer = setInterval(() => setSecsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [secsLeft]);

  const applyOtpDigits = useCallback((raw: string) => {
    const fullCode = parseOtpDigits(raw, OTP_LENGTH);
    if (!fullCode) {
      return;
    }
    setError('');
    setCode(fullCode);
    inputsRef.current[getOtpFocusIndex(fullCode, OTP_LENGTH)]?.focus();
  }, []);

  const handleChange = (index: number, raw: string) => {
    const digits = parseOtpDigits(raw, OTP_LENGTH);
    setError('');

    if (digits.length > 1) {
      applyOtpDigits(digits);
      return;
    }

    const digit = digits.slice(-1);
    const nextCode = mergeOtpDigit(code, index, digit, OTP_LENGTH);
    setCode(nextCode);
    if (digit && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleInput = (index: number, e: React.FormEvent<HTMLInputElement>) => {
    const raw = (e.currentTarget as HTMLInputElement).value;
    if (parseOtpDigits(raw, OTP_LENGTH).length > 1 || (index === 0 && raw.length > 1)) {
      applyOtpDigits(raw);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = parseOtpDigits(e.clipboardData.getData('text'), OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    applyOtpDigits(text);
  };

  const verifyingRef = useRef(false);

  const submitCode = useCallback(async (otpCode: string) => {
    if (otpCode.length !== OTP_LENGTH || verifyingRef.current) {
      return;
    }
    verifyingRef.current = true;
    setVerifying(true);
    setError('');
    const ok = await onVerify(otpCode);
    if (!ok) {
      setError('That code didn’t work. Please try again.');
      setCode('');
      verifyingRef.current = false;
      setVerifying(false);
      inputsRef.current[0]?.focus();
    }
  }, [onVerify]);

  useEffect(() => {
    if (code.length === OTP_LENGTH && !verifyingRef.current) {
      void submitCode(code);
    }
  }, [code, submitCode]);

  const handleVerify = () => {
    void submitCode(code);
  };

  const canVerify = code.length === OTP_LENGTH && !verifying;

  return (
    <div style={{
      margin: '4px 0 14px 36px', padding: '12px 16px',
      background: '#f4f0eb', borderRadius: 12, border: '1px solid #d4cec8',
      fontSize: 13, color: '#3a3530',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Verify your mobile number</div>
      <div style={{ color: '#6b5f54', marginBottom: 10 }}>
        Enter the 6-digit code we sent to {mobile || 'your mobile'}.
        {' '}Your phone may fill this in automatically when the SMS arrives.
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
          <input
            key={index}
            ref={(el) => { inputsRef.current[index] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            name={index === 0 ? 'otp' : undefined}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={index === 0 ? OTP_LENGTH : 1}
            value={code[index] ?? ''}
            disabled={verifying}
            onChange={(e) => handleChange(index, e.target.value)}
            onInput={(e) => handleInput(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{
              width: 36, height: 44, textAlign: 'center', fontSize: 18,
              fontFamily: 'inherit', color: '#2d2925',
              border: `1.5px solid ${error ? '#c0392b' : '#d4cec8'}`,
              borderRadius: 8, background: '#fff', outline: 'none',
            }}
          />
        ))}
      </div>
      {error && (
        <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{error}</div>
      )}
      <button
        onClick={handleVerify}
        disabled={!canVerify}
        style={{
          padding: '8px 18px', borderRadius: 20, border: 'none',
          background: canVerify ? '#2d2925' : '#c8c1ba', color: '#fff',
          fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
          cursor: canVerify ? 'pointer' : 'default',
        }}
      >
        {verifying ? 'Verifying…' : 'Verify'}
      </button>
      <div style={{ fontSize: 12, color: '#9a8f86', marginTop: 8 }}>
        {secsLeft > 0
          ? `Didn’t get a code? You can resend in ${secsLeft}s.`
          : 'Didn’t get a code? Check your messages and try again.'}
      </div>
    </div>
  );
}

function downscaleImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read-failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode-failed'));
      img.onload = () => {
        const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(reader.result as string);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #3a3530 0%, #6b5f54 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 600, color: '#e8e2da', marginTop: 2,
      }}>H</div>
      <div style={{
        padding: '12px 16px', borderRadius: '18px 18px 18px 4px',
        background: '#f4f0eb', display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <style>{`
          @keyframes dot-bounce {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            40% { transform: translateY(-5px); opacity: 1; }
          }
        `}</style>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: '50%', background: '#9a8f86', display: 'block',
            animation: `dot-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
}

export default function ChatWindow({
  messages,
  loading,
  requiresAction,
  onSend,
  onVerifyOtp,
  sessionId,
  onDepositPaid,
  onAddToCart,
  cartState,
  cartCount = null,
  voiceSupported = false,
  voiceMode = false,
  isListening = false,
  isSpeaking = false,
  isAwaitingReply = false,
  onToggleVoice,
  voiceError = null,
}: ChatWindowProps) {
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [imageError, setImageError] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = messagesRef.current;
    if (!container) {
      return;
    }
    if (behavior === 'smooth') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, []);

  useEffect(() => {
    scrollMessagesToBottom('smooth');
  }, [messages, loading, requiresAction, scrollMessagesToBottom]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return undefined;
    }

    const onViewportChange = () => {
      scrollMessagesToBottom('auto');
    };

    viewport.addEventListener('resize', onViewportChange);
    viewport.addEventListener('scroll', onViewportChange);

    return () => {
      viewport.removeEventListener('resize', onViewportChange);
      viewport.removeEventListener('scroll', onViewportChange);
    };
  }, [scrollMessagesToBottom]);

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }
    if (draft) {
      applyTextareaResize(textareaRef.current);
      return;
    }
    resetTextareaResize(textareaRef.current);
  }, [draft]);

  const handleSend = () => {
    const text = draft.trim();
    if ((!text && pendingImages.length === 0) || loading) return;
    setDraft('');
    onSend(text, pendingImages.length ? pendingImages : undefined);
    setPendingImages([]);
    setImageError('');
  };

  const handleTextareaFocus = () => {
    // Two frames: first after focus, second after keyboard layout starts settling.
    requestAnimationFrame(() => {
      scrollMessagesToBottom('auto');
      requestAnimationFrame(() => {
        scrollMessagesToBottom('auto');
      });
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - pendingImages.length;
    if (remaining <= 0) {
      setImageError(`You can attach up to ${MAX_IMAGES} photos.`);
      return;
    }

    let hitLimit = files.length > remaining;
    const accepted: string[] = [];
    let sawError = '';

    for (const file of files.slice(0, remaining)) {
      if (!file.type.startsWith('image/')) {
        sawError = 'Please choose image files only.';
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        sawError = 'Some images were too large (max 8MB each).';
        continue;
      }
      try {
        accepted.push(await downscaleImage(file));
      } catch {
        sawError = 'Could not read one of those images. Try another.';
      }
    }

    if (accepted.length) {
      setPendingImages((prev) => [...prev, ...accepted].slice(0, MAX_IMAGES));
    }
    if (hitLimit) {
      setImageError(`You can attach up to ${MAX_IMAGES} photos.`);
    } else {
      setImageError(sawError);
    }
  };

  const removePendingImage = (index: number) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
    setImageError('');
  };

  const canSend = (!!draft.trim() || pendingImages.length > 0) && !loading;
  const atImageLimit = pendingImages.length >= MAX_IMAGES;
  const photoPickerDisabled = atImageLimit;

  const needsTextFallback = requiresAction?.type === 'otp_input'
    || requiresAction?.type === 'deposit_payment';
  const showVoicePanel = voiceMode && !needsTextFallback;

  const renderActionBlocks = () => (
    <>
      {requiresAction?.type === 'booking_confirmed' && (
        <div style={{
          margin: '4px 0 14px 36px', padding: '12px 16px',
          background: '#f0faf4', borderRadius: 12, border: '1px solid #b7e4c7',
          fontSize: 13, color: '#1b6b35', fontWeight: 500,
        }}>
          Booking confirmed — check your messages for details.
        </div>
      )}
      {requiresAction?.type === 'otp_input' && onVerifyOtp && (
        <OtpInline mobile={requiresAction.mobile} onVerify={onVerifyOtp} />
      )}
      {requiresAction?.type === 'deposit_payment' && sessionId && onDepositPaid && (
        <AgentInlineDeposit
          sessionId={sessionId}
          amount={requiresAction.amount || '0.00'}
          currency={(requiresAction.currency || 'AUD').toUpperCase()}
          onPaid={onDepositPaid}
        />
      )}
      {!voiceMode && requiresAction?.type === 'treatment_upsell' && (
        <div style={{
          margin: '4px 0 14px 36px', padding: '12px 16px',
          background: '#fdf8f0', borderRadius: 12, border: '1px solid #e8d5b0',
          fontSize: 13, color: '#3a3530',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{requiresAction.title}</div>
          <div style={{ color: '#6b5f54', marginBottom: 10 }}>{requiresAction.description}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onSend('Yes, add it')} style={{
              padding: '7px 16px', borderRadius: 20, border: 'none',
              background: '#2d2925', color: '#fff', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Add</button>
            <button onClick={() => onSend('No thanks')} style={{
              padding: '7px 16px', borderRadius: 20, border: '1.5px solid #d4cec8',
              background: '#fff', color: '#3a3530', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Skip</button>
          </div>
        </div>
      )}
      {!voiceMode
        && requiresAction?.type === 'product_recommendation'
        && (requiresAction.products?.length ?? 0) > 0 && (
        <ProductCardsInline
          products={requiresAction.products as AgentProduct[]}
          onAddToCart={onAddToCart}
          cartState={cartState}
        />
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {showVoicePanel ? (
        <VoiceModePanel
          isListening={isListening}
          isSpeaking={isSpeaking}
          isAwaitingReply={isAwaitingReply}
          loading={loading}
        />
      ) : (
        <div
          ref={messagesRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px 16px 8px',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              role={msg.role}
              content={msg.content}
              images={msg.images}
              quickReplies={voiceMode ? undefined : msg.quick_replies}
              onQuickReply={!loading && !voiceMode ? (value) => onSend(value) : undefined}
            />
          ))}
          {loading && !voiceMode && <TypingDots />}
          {renderActionBlocks()}
        </div>
      )}

      <div style={{
        padding: '10px 14px calc(14px + env(safe-area-inset-bottom, 0px))',
        borderTop: '1px solid #ede9e3',
        background: '#faf8f5',
        flexShrink: 0,
      }}>
        {!showVoicePanel && pendingImages.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {pendingImages.map((image, index) => (
              <div
                key={index}
                style={{ position: 'relative', width: 56, height: 56 }}
              >
                <img
                  src={image}
                  alt={`Selected hair photo ${index + 1}`}
                  style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', display: 'block' }}
                />
                <button
                  onClick={() => removePendingImage(index)}
                  aria-label={`Remove photo ${index + 1}`}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 20, height: 20, borderRadius: '50%', border: 'none',
                    background: '#2d2925', color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 18 18" fill="none">
                    <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {!showVoicePanel && imageError && (
          <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{imageError}</div>
        )}
        {!showVoicePanel && (cartCount ?? 0) > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
            padding: '8px 12px', background: '#f0faf4', border: '1px solid #b7e4c7',
            borderRadius: 12, fontSize: 12.5, color: '#1b6b35', fontWeight: 500,
          }}>
            <span style={{ flex: 1 }}>
              {cartCount} item{cartCount === 1 ? '' : 's'} in your cart
            </span>
            <button
              onClick={() => {
                // Prefer the theme's slide-over drawer (stays in the chat);
                // fall back to navigating to the cart page.
                if (!openShopifyCartDrawer()) {
                  window.location.href = `${getShopifyCartRoot()}cart`;
                }
              }}
              style={{
                color: '#1b6b35', fontWeight: 600, textDecoration: 'underline',
                fontSize: 12, background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              View cart
            </button>
            <a
              href={`${getShopifyCartRoot()}checkout`}
              style={{
                background: '#2d2925', color: '#fff', borderRadius: 16,
                padding: '6px 14px', fontSize: 12, fontWeight: 600, textDecoration: 'none',
              }}
            >
              Checkout
            </a>
          </div>
        )}
        {voiceError && (
          <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{voiceError}</div>
        )}
        {needsTextFallback && (
          <div style={{ fontSize: 12, color: '#9a8f86', marginBottom: 8 }}>
            {requiresAction?.type === 'otp_input'
              ? 'Enter the SMS code above to continue. Voice is paused during verification.'
              : 'Complete the payment step above to continue.'}
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: showVoicePanel ? 'center' : 'flex-end',
          justifyContent: showVoicePanel ? 'center' : 'flex-end',
          gap: 10,
          background: showVoicePanel ? 'transparent' : '#f4f0eb',
          borderRadius: 24,
          padding: showVoicePanel ? '8px 0' : '8px 8px 8px 12px',
          position: 'relative',
          zIndex: 1,
        }}>
          {!showVoicePanel && (
            <>
              <input
                id={PHOTO_INPUT_ID}
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                disabled={photoPickerDisabled}
                style={{ display: 'none' }}
              />
              <label
                htmlFor={photoPickerDisabled ? undefined : PHOTO_INPUT_ID}
                aria-label={atImageLimit ? `Photo limit reached (${MAX_IMAGES})` : 'Add photos of your hair'}
                title={atImageLimit ? `You can attach up to ${MAX_IMAGES} photos` : 'Add photos of your hair'}
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  color: '#6b5f54',
                  cursor: photoPickerDisabled ? 'default' : 'pointer',
                  opacity: photoPickerDisabled ? 0.4 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, margin: 0,
                  pointerEvents: photoPickerDisabled ? 'none' : 'auto',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                  <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.7" />
                </svg>
              </label>
            </>
          )}
          {voiceSupported && !needsTextFallback && (
            <>
              <style>{`
                @keyframes mic-pulse {
                  0% { box-shadow: 0 0 0 0 rgba(192,57,43,0.45); }
                  70% { box-shadow: 0 0 0 8px rgba(192,57,43,0); }
                  100% { box-shadow: 0 0 0 0 rgba(192,57,43,0); }
                }
              `}</style>
              <button
                type="button"
                onClick={onToggleVoice}
                aria-label={voiceMode ? 'Switch to typing' : 'Talk to the assistant'}
                title={voiceMode ? 'Switch to typing' : 'Talk'}
                style={{
                  width: showVoicePanel ? 56 : 34,
                  height: showVoicePanel ? 56 : 34,
                  borderRadius: '50%', border: 'none', flexShrink: 0,
                  background: voiceMode ? (isSpeaking ? '#2d7a4a' : '#c0392b') : '#e7e1d9',
                  color: voiceMode ? '#fff' : '#6b5f54',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                  animation: voiceMode && isListening ? 'mic-pulse 1.4s ease-out infinite' : 'none',
                  boxShadow: showVoicePanel ? '0 4px 16px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                <svg width={showVoicePanel ? 22 : 16} height={showVoicePanel ? 22 : 16} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}
          {!showVoicePanel && (
            <>
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={handleTextareaFocus}
                onKeyDown={handleKeyDown}
                disabled={loading}
                style={{
                  flex: 1, border: 'none', background: 'transparent', outline: 'none',
                  resize: 'none', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit',
                  color: '#2d2925', minHeight: CHAT_TEXTAREA_MIN_HEIGHT,
                  maxHeight: CHAT_TEXTAREA_MAX_HEIGHT, overflowY: 'hidden',
                  padding: '6px 0',
                }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                style={{
                  width: 34, height: 34, borderRadius: '50%', border: 'none',
                  background: canSend ? '#2d2925' : '#c8c1ba',
                  color: '#fff', cursor: canSend ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.2s',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 13V3M8 3L3 8M8 3l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#b0a89f', marginTop: 8 }}>
          {showVoicePanel ? 'Tap the microphone to switch to typing' : 'Powered by HUSL AI'}
        </div>
      </div>
    </div>
  );
}
